import * as React from "react";

/**
 * Minimal hand-rolled icon set (no icon-library dependency). Every icon is a plain
 * `<svg>` sized to `1em` so it inherits the surrounding text colour and size. These
 * exist primarily so status can be read by *shape*, not only by colour — see
 * `StatusPill`.
 */
export type IconProps = React.SVGProps<SVGSVGElement>;

function base(children: React.ReactNode, props: IconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const IconDot = (p: IconProps) => base(<circle cx="8" cy="8" r="3.4" fill="currentColor" stroke="none" />, p);
export const IconRing = (p: IconProps) => base(<circle cx="8" cy="8" r="4.6" />, p);
export const IconCheck = (p: IconProps) => base(<path d="M3 8.5l3.2 3.2L13 4.8" />, p);
export const IconCross = (p: IconProps) => base(<path d="M4 4l8 8M12 4l-8 8" />, p);
export const IconTriangle = (p: IconProps) =>
  base(
    <>
      <path d="M8 2.6l6.2 10.8H1.8L8 2.6z" />
      <path d="M8 6.6v3.2" />
      <circle cx="8" cy="11.4" r="0.15" fill="currentColor" />
    </>,
    p,
  );
export const IconSquare = (p: IconProps) => base(<rect x="3.6" y="3.6" width="8.8" height="8.8" rx="1.4" />, p);
export const IconHalf = (p: IconProps) =>
  base(
    <>
      <circle cx="8" cy="8" r="4.6" />
      <path d="M8 3.4a4.6 4.6 0 010 9.2z" fill="currentColor" stroke="none" />
    </>,
    p,
  );
export const IconMute = (p: IconProps) =>
  base(
    <>
      <path d="M2 6.4h2.6L8.4 3v10L4.6 9.6H2z" />
      <path d="M10.4 5.6l3.4 4.8M13.8 5.6l-3.4 4.8" />
    </>,
    p,
  );
export const IconChevronDown = (p: IconProps) => base(<path d="M3.5 5.5L8 10l4.5-4.5" />, p);
export const IconCopy = (p: IconProps) =>
  base(
    <>
      <rect x="5.6" y="5.6" width="7.4" height="7.4" rx="1.1" />
      <path d="M3 10.4V3.6a1 1 0 011-1h6.8" />
    </>,
    p,
  );
export const IconExternal = (p: IconProps) =>
  base(
    <>
      <path d="M6.6 3H13v6.4" />
      <path d="M13 3L6.8 9.2" />
      <path d="M11 9.4v2.6a1 1 0 01-1 1H3.6a1 1 0 01-1-1V5a1 1 0 011-1h2.7" />
    </>,
    p,
  );
export const IconAlert = (p: IconProps) =>
  base(
    <>
      <path d="M8 2.6l6.2 10.8H1.8L8 2.6z" />
      <path d="M8 6.4v2.8" />
      <circle cx="8" cy="11" r="0.4" fill="currentColor" stroke="none" />
    </>,
    p,
  );
export const IconLock = (p: IconProps) =>
  base(
    <>
      <rect x="3.4" y="7.2" width="9.2" height="6.2" rx="1.2" />
      <path d="M5.2 7.2V5a2.8 2.8 0 015.6 0v2.2" />
    </>,
    p,
  );
export const IconInfo = (p: IconProps) =>
  base(
    <>
      <circle cx="8" cy="8" r="5.6" />
      <path d="M8 7.2v3.4" />
      <circle cx="8" cy="5.3" r="0.15" fill="currentColor" />
    </>,
    p,
  );
export const IconClock = (p: IconProps) =>
  base(
    <>
      <circle cx="8" cy="8" r="5.6" />
      <path d="M8 4.8V8l2.4 1.4" />
    </>,
    p,
  );
export const IconSearch = (p: IconProps) =>
  base(
    <>
      <circle cx="7" cy="7" r="4.2" />
      <path d="M13 13l-2.9-2.9" />
    </>,
    p,
  );
export const IconRefresh = (p: IconProps) =>
  base(
    <>
      <path d="M12.6 6.4A4.8 4.8 0 003.4 8" />
      <path d="M3.4 9.6A4.8 4.8 0 0012.6 8" />
      <path d="M12.6 3.4v3h-3M3.4 12.6v-3h3" />
    </>,
    p,
  );
export const IconSpinner = (p: IconProps) =>
  base(<path d="M8 2.4a5.6 5.6 0 105.6 5.6" />, p);
